import { SeoFallbackInputsType, SeoFallbackSource, SeoFallbackType, SeoField } from "./seoTypes";

interface ChainStepType {
  source: SeoFallbackSource;
  value?: string;
}

/** True for a value that is set and not blank. */
export function isFilled(value?: string | null): value is string {
  return value != null && value.trim() !== "";
}

/*
 * Mirrors the backend's OG image rule (SeoMetadataService): an http(s) URL with a plain host, which
 * java.net.URI can parse. A browser would load some URLs the backend refuses, e.g. with a space or
 * "|", so the editor flags those before Save instead of showing a preview the save then rejects.
 */
const WEB_URL_PREFIX = /^https?:\/\/[a-z0-9._~-]+(:[0-9]+)?([/?#]|$)/i;
const URI_UNPARSEABLE = /[\s"<>\\^`{|}\p{Cc}]|%(?![0-9a-f]{2})|#.*#|^[^?#]*[[\]]/iu;
const PROTOCOL_RELATIVE_PREFIX = "//";

/**
 * The URL the backend stores for an OG image value: trimmed, with a protocol-relative URL made
 * https. Undefined when the backend would reject the value.
 */
export function toWebUrl(value?: string): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  const trimmed = value.trim();
  const url = trimmed.startsWith(PROTOCOL_RELATIVE_PREFIX) ? `https:${trimmed}` : trimmed;
  return WEB_URL_PREFIX.test(url) && !URI_UNPARSEABLE.test(url) ? url : undefined;
}

/** True for a value the backend stores as an OG image. */
export function isWebUrl(value?: string): value is string {
  return toWebUrl(value) !== undefined;
}

function chainFor(field: SeoField, inputs: SeoFallbackInputsType): ChainStepType[] {
  // Defaults carry no OG image, so that field's chain ends before them.
  const defaultValue = field === "ogImageUrl" ? undefined : inputs.defaults?.[field];
  return [
    { source: "tabCms", value: inputs.tabCms?.[field] },
    { source: "pageCms", value: inputs.pageCms?.[field] },
    { source: "game", value: inputs.game?.[field] },
    { source: "default", value: defaultValue },
  ];
}

/**
 * Resolves the value an empty SEO field in one locale falls back to on the player site: tab CMS (tabs
 * only), then page CMS, then the values derived from the game (game pages only), then the default. The
 * CMS values come from Strapi. The target's own admin value comes before all of these, but the editor
 * only asks for a fallback when that value is empty. The player frontend must apply the same chain, or
 * what the editor promises is not what players get.
 *
 * Blank strings count as unset. Returns undefined when no step has a value.
 */
export function resolveFallback(field: SeoField, inputs: SeoFallbackInputsType): SeoFallbackType | undefined {
  const step = chainFor(field, inputs).find((candidate) => isFilled(candidate.value));
  if (step === undefined || step.value === undefined) {
    return undefined;
  }
  return { value: step.value, source: step.source };
}
